import { el, clear, toast } from '../../dom';
import { api } from '../../../core/network/api';
import {
  btn, checkbox, labelled, loadInto, panel, pill, table, toastError, input,
} from './ui';

interface DepositMethod {
  id: string;
  label: string;
  configured: boolean;
  enabled: boolean;
  active: boolean;
  currencies: string[];
  currencyOptions: string[];
  billing: 'required' | 'optional';
  minBilling: 'required' | 'optional';
}

interface PayoutMethod {
  id: string;
  label: string;
  receiver: 'cardNumber' | 'iban';
  configured: boolean;
  enabled: boolean;
  active: boolean;
  currencies: string[];
  pollable: boolean;
}

interface PaymentLimits {
  depositMinCents: number;
  depositMaxCents: number;
  depositFeeBps: number;
  depositFeePct: number;
  depositKycThresholdCents: number;
  requireVerifiedEmail: boolean;
  withdrawalMinCents: number;
  withdrawalMaxCents: number;
  withdrawalFeeBps: number;
  withdrawalFeePct: number;
  capToDeposits: boolean;
}

interface PaymentConfig {
  adopted: boolean;
  currencies: string[];
  defaultCurrency: string;
  withdrawalsEnabled: boolean;
  kycEnabled: boolean;
  deposit: DepositMethod[];
  payout: PayoutMethod[];
  limits: PaymentLimits;
}

const CONFIG = '/admin/payments/config';

function statusPill(cfg: PaymentConfig, kind: 'deposit' | 'payout', m: DepositMethod | PayoutMethod): HTMLElement {
  if (!m.configured) return pill('Not configured', 'bad');
  if (m.active) return pill('Active', 'ok');
  if (!m.enabled) return pill('Disabled', 'mute');
  if (!cfg.adopted) return pill('Not adopted', 'warn');
  if (m.currencies.length === 0) return pill('No currency', 'warn');
  if (kind === 'payout' && !cfg.withdrawalsEnabled) return pill('Withdrawals off', 'warn');
  return pill('Inactive', 'warn');
}

function methodToggle(
  kind: 'deposit' | 'payout',
  m: DepositMethod | PayoutMethod,
  patch: (body: Record<string, unknown>, revert: () => void) => void,
): HTMLElement {
  const cb = checkbox('', m.enabled);
  if (!m.configured) {
    cb.input.disabled = true;
    cb.input.title = 'Terminal credentials missing — contact ops';
    cb.wrap.classList.add('opacity-40', 'cursor-not-allowed');
    return cb.wrap;
  }
  cb.input.addEventListener('change', () => {
    const enabled = cb.input.checked;
    cb.input.disabled = true;
    patch(
      { [kind]: { [m.id]: { enabled } } },
      () => { cb.input.checked = !enabled; },
    );
    setTimeout(() => { cb.input.disabled = false; }, 300);
  });
  return cb.wrap;
}

function billingSelect(m: DepositMethod, patch: (body: Record<string, unknown>, revert: () => void) => void): HTMLElement {
  if (m.minBilling === 'required') {
    return el('span', 'text-xs text-text-dim', {}, 'Required');
  }
  const sel = document.createElement('select');
  sel.className = 'rounded border border-border bg-surface-alt px-2 py-1 text-xs text-text';
  for (const v of ['required', 'optional'] as const) {
    const opt = document.createElement('option');
    opt.value = v;
    opt.textContent = v.charAt(0).toUpperCase() + v.slice(1);
    if (v === m.billing) opt.selected = true;
    sel.appendChild(opt);
  }
  sel.addEventListener('change', () => {
    const prev = m.billing;
    patch(
      { deposit: { [m.id]: { billing: sel.value } } },
      () => { sel.value = prev; },
    );
  });
  return sel;
}

const eur = (cents: number) => (cents / 100).toFixed(2);

export function renderPaymentsConfig(host: HTMLElement): () => void {
  let disposed = false;
  let cfg: PaymentConfig | null = null;

  function patch(body: Record<string, unknown>, revert: () => void) {
    api.patch<PaymentConfig>(CONFIG, body)
      .then((next) => {
        if (disposed) return;
        cfg = next;
        toast('Saved');
        render();
      })
      .catch((err: unknown) => {
        if (disposed) return;
        revert();
        toastError(err, 'Failed to update');
      });
  }

  function render() {
    if (disposed || !cfg) return;
    clear(host);
    const c = cfg;

    if (!c.adopted) {
      host.appendChild(el('div', 'rounded border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-xs text-amber-200 mb-4', {},
        'Core payments not adopted — no method is active regardless of toggles.'));
    }

    host.appendChild(el('div', 'text-xs text-text-dim mb-4', {},
      `Currencies: ${c.currencies.join(', ')} (default ${c.defaultCurrency})`));

    // Deposit methods
    const depRows: (Node | string)[][] = c.deposit.map((m) => [
      m.label,
      m.currencies.length ? m.currencies.join(', ') : '—',
      billingSelect(m, patch),
      methodToggle('deposit', m, patch),
      statusPill(c, 'deposit', m),
    ]);
    host.appendChild(panel('Deposit methods',
      table(['Method', 'Currencies', 'Billing', 'Enabled', 'Status'], depRows),
    ));

    // Payout methods
    const payRows: (Node | string)[][] = c.payout.map((m) => [
      m.label,
      m.currencies.length ? m.currencies.join(', ') : '—',
      'Required',
      methodToggle('payout', m, patch),
      statusPill(c, 'payout', m),
    ]);
    const payoutNote = !c.withdrawalsEnabled
      ? el('div', 'text-xs text-amber-300/70 mt-2', {}, 'Withdrawals are disabled — payout methods will not be active.')
      : null;
    host.appendChild(panel('Payout methods', table(['Method', 'Currencies', 'Billing', 'Enabled', 'Status'], payRows), payoutNote));

    // Withdrawal toggle
    const wdToggle = checkbox('Withdrawals enabled', c.withdrawalsEnabled);
    wdToggle.input.addEventListener('change', () => {
      const on = wdToggle.input.checked;
      patch({ withdrawalsEnabled: on }, () => { wdToggle.input.checked = !on; });
    });
    const capToggle = checkbox('Cap withdrawals to deposited amount', c.limits.capToDeposits);
    capToggle.input.addEventListener('change', () => {
      const on = capToggle.input.checked;
      patch({ limits: { capToDeposits: on } }, () => { capToggle.input.checked = !on; });
    });
    const emailToggle = checkbox('Require verified e-mail to deposit', c.limits.requireVerifiedEmail);
    emailToggle.input.addEventListener('change', () => {
      const on = emailToggle.input.checked;
      patch({ limits: { requireVerifiedEmail: on } }, () => { emailToggle.input.checked = !on; });
    });

    // Limits forms
    const depMin = input('Min EUR', eur(c.limits.depositMinCents), 'number');
    const depMax = input('Max EUR', eur(c.limits.depositMaxCents), 'number');
    const depFee = input('Fee %', String(c.limits.depositFeePct), 'number');
    const depKyc = input('KYC threshold EUR', eur(c.limits.depositKycThresholdCents), 'number');
    const saveDepLimits = btn('Save deposit limits', () => {
      const limits: Record<string, number> = {
        depositMinCents: Math.round(Number(depMin.value) * 100),
        depositMaxCents: Math.round(Number(depMax.value) * 100),
        depositFeePct: Number(depFee.value),
        depositKycThresholdCents: Math.round(Number(depKyc.value) * 100),
      };
      patch({ limits }, () => {});
    }, 'primary');

    const wdMin = input('Min EUR', eur(c.limits.withdrawalMinCents), 'number');
    const wdMax = input('Max EUR', eur(c.limits.withdrawalMaxCents), 'number');
    const wdFee = input('Fee %', String(c.limits.withdrawalFeePct), 'number');
    const saveWdLimits = btn('Save withdrawal limits', () => {
      const limits: Record<string, number> = {
        withdrawalMinCents: Math.round(Number(wdMin.value) * 100),
        withdrawalMaxCents: Math.round(Number(wdMax.value) * 100),
        withdrawalFeePct: Number(wdFee.value),
      };
      patch({ limits }, () => {});
    }, 'primary');

    const limitsGrid = (items: HTMLElement[]) => {
      const g = el('div', 'grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2');
      items.forEach((i) => g.appendChild(i));
      return g;
    };

    host.appendChild(panel('Withdrawals',
      wdToggle.wrap, capToggle.wrap,
      limitsGrid([labelled('Min (EUR)', wdMin), labelled('Max (EUR)', wdMax), labelled('Fee %', wdFee)]),
      el('div', 'flex justify-end mt-3', {}, saveWdLimits),
    ));

    host.appendChild(panel('Deposit limits & fees',
      emailToggle.wrap,
      limitsGrid([labelled('Min (EUR)', depMin), labelled('Max (EUR)', depMax), labelled('Fee %', depFee), labelled('KYC threshold (EUR)', depKyc)]),
      el('div', 'flex justify-end mt-3', {}, saveDepLimits),
    ));
  }

  return loadInto(host, () => api.get<PaymentConfig>(CONFIG), (config) => {
    cfg = config;
    render();
    return el('span');
  }, 'Failed to load payment configuration.');
}
